import React, {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  MelaButton,
  MelaModalFooter,
  MelaModalHeader,
  MelaModal,
  type MelaModalProps,
} from "@/ui";

export interface MelaDialogProps
  extends Omit<MelaModalProps, "children" | "open" | "show" | "onClose"> {
  title: string;
  open?: boolean;
  show?: boolean;
  onClose?: (...args: any[]) => void;
  onSave?: () => void;
  onSaveLabel?: string;
  onCloseLabel?: string;
  disabledSave?: boolean;
  saveLoading?: boolean;
  saveHidden?: boolean;
  footerContainerStyle?: CSSProperties;
  footerContainerClassName?: string;
  footerStartComponent?: ReactNode;
  // Between Cancel and Save: footer order is reading order, so secondary actions go after Cancel.
  footerMiddleComponent?: ReactNode;
  headerRightComponent?: ReactNode;
  showCloseButton?: boolean;
}

export const MelaDialog = ({
  title,
  open,
  show,
  onClose,
  onSave,
  onSaveLabel = "button.save.translation",
  onCloseLabel = "button.cancel.translation",
  disabledSave = false,
  saveLoading = false,
  saveHidden = false,
  footerContainerStyle,
  footerContainerClassName = "",
  footerStartComponent,
  footerMiddleComponent,
  headerRightComponent,
  showCloseButton = false,
  children,
  ...modalProps
}: PropsWithChildren<MelaDialogProps>) => {
  const { t } = useTranslation();
  const resolvedOpen = open ?? show ?? true;
  const showCloseAction = showCloseButton && Boolean(onClose);

  return (
    <MelaModal {...modalProps} open={resolvedOpen} onClose={onClose}>
      <MelaModalHeader
        title={title}
        RightComponent={headerRightComponent}
        showCloseButton={showCloseAction}
        closeButtonAriaLabel={t("button.close.translation")}
        onClose={() => onClose?.()}
      />
      {children}
      <MelaModalFooter
        containerStyle={footerContainerStyle}
        containerClassName={footerContainerClassName}
      >
        {footerStartComponent}
        <MelaButton onClick={() => onClose?.()} variant="soft" tone="neutral">
          {t(onCloseLabel)}
        </MelaButton>
        {footerMiddleComponent}
        {!saveHidden && onSave ? (
          <MelaButton
            onClick={onSave}
            variant="solid"
            tone="primary"
            disabled={disabledSave || saveLoading}
            loading={saveLoading}
          >
            {t(onSaveLabel)}
          </MelaButton>
        ) : null}
      </MelaModalFooter>
    </MelaModal>
  );
};
